import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
}

const DEFAULT_OG_IMAGE = '/og-image.png';
const SITE_NAME = 'Sorvaine';
const BASE_URL = 'https://sorvaine.com';

export function SEO({ 
  title, 
  description, 
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false 
}: SEOProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper to update or create meta tag
    const updateMeta = (selector: string, attribute: string, content: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '');
        } else if (selector.includes('name=')) {
          element.setAttribute('name', selector.match(/name="([^"]+)"/)?.[1] || '');
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, content);
    };

    // Basic meta tags
    updateMeta('meta[name="description"]', 'content', description);
    
    // Robots
    if (noIndex) {
      updateMeta('meta[name="robots"]', 'content', 'noindex, nofollow');
    } else {
      updateMeta('meta[name="robots"]', 'content', 'index, follow');
    }

    // Open Graph
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="og:image"]', 'content', ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`);
    if (canonical) {
      updateMeta('meta[property="og:url"]', 'content', canonical);
    }

    // Locale
    updateMeta('meta[property="og:locale"]', 'content', 'en_GB');

    // Twitter
    updateMeta('meta[name="twitter:title"]', 'content', title);
    updateMeta('meta[name="twitter:description"]', 'content', description);
    updateMeta('meta[name="twitter:image"]', 'content', ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`);

    // Canonical link
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Cleanup function to restore defaults
    return () => {
      document.title = 'Sorvaine — Bid & Tender Management Software';
    };
  }, [title, description, canonical, ogImage, noIndex]);

  return null;
}
